<template>
  <vx-card no-shadow card-border>
    <template #no-body>
      <div class="px-6 pt-6">
        <h3 >
          {{ $t('FormTitle') }}
        </h3>
      </div>

      <vs-divider class="mt-6 mb-0"></vs-divider>

      <div class="vx-row mb-6 flex-col xl:flex-row">
        <div class="vx-col w-full xl:w-1/4 branding-left-col">
          <div class="pl-6 pt-6 pr-6 xl:pr-0 h-full flex flex-col justify-between">

            <div class="w-full">
              <label class="font-bold">{{ $t('BrandElementsTitle') }}</label>
              <p>
                {{ $t('BrandElementsText', {
                  businessName: tenantAccountDetails.name
                }) }}
              </p>

              <vs-divider></vs-divider>

              <div class="vx-row">
                <div class="vx-col w-1/2 flex items-center">
                  <label class="font-bold">
                    <span class="inline-flex">
                      <span>
                        {{ $t('BrandingIcon') }}
                      </span>

                      <vx-tooltip
                        :text="$t('BrandingIconHelpText')"
                        position="top"
                        class="inline-block ml-2">
                        <feather-icon
                          icon="HelpCircleIcon"
                          svgClasses="h-4 w-4 hover:text-primary cursor-pointer"/>
                      </vx-tooltip>
                    </span>
                  </label>
                </div>
                <div class="vx-col w-1/2 flex items-center justify-end">
                  <vs-input
                    v-model="model.icon"
                    class="w-full"
                    type="hidden"
                    :name="`${$t('BrandingIcon')}Validate`"
                    v-validate="''"
                    data-vv-validate-on="blur|input"/>
                  <uploader-preview-image
                    v-model="model.icon"
                    :minimalist="true"
                    :show-only-icon="true"
                    upload-icon="PlusIcon"
                    :upload-icon-size="8"
                    :base-path="basePath"
                    file-name="icon"
                    :file-input-name="$t('BrandingIcon')"
                    :accept-ext="acceptedFileExts"
                    preview-image-size="50"
                    show-remove-image
                  />
                </div>
              </div>

              <div class="vx-row mt-base">
                <div class="vx-col w-1/2 flex items-center">
                  <label class="font-bold">
                  <span class="inline-flex">
                    <span>
                      {{ $t('BrandingLogo') }}
                    </span>

                    <vx-tooltip
                      :text="$t('BrandingLogoHelpText')"
                      position="top"
                      class="inline-block ml-2">
                      <feather-icon
                        icon="HelpCircleIcon"
                        svgClasses="h-4 w-4 hover:text-primary cursor-pointer"/>
                    </vx-tooltip>
                  </span>
                  </label>
                </div>

                <div class="vx-col w-1/2 flex items-center justify-end">
                  <vs-input
                    v-model="model.icon"
                    class="w-full"
                    type="hidden"
                    :name="`${$t('BrandingLogo')}Validate`"
                    v-validate="''"
                    data-vv-validate-on="blur|input"/>
                  <uploader-preview-image
                    v-model="model.logo"
                    :minimalist="true"
                    :show-only-icon="true"
                    upload-icon="PlusIcon"
                    :upload-icon-size="8"
                    :base-path="basePath"
                    file-name="logo"
                    :file-input-name="$t('BrandingLogo')"
                    :accept-ext="acceptedFileExts"
                    preview-image-size="150"
                    show-remove-image
                  />
                </div>
              </div>

              <div class="vx-row mt-base">
                <div class="vx-col w-2/3 flex items-center">
                  <label class="font-bold">
                    <span class="inline-flex items-center whitespace-no-wrap">
                      <span>{{ $t('PreferLogoText') }}</span>
                      <vx-tooltip
                        :text="$t('PreferLogoHelpText')"
                        position="top"
                        class="inline-block ml-2">
                        <feather-icon
                          icon="HelpCircleIcon"
                          svgClasses="h-4 w-4 hover:text-primary cursor-pointer"/>
                      </vx-tooltip>
                    </span>
                  </label>
                </div>

                <div class="vx-col w-1/3 flex items-center justify-end">
                  <vs-checkbox
                    v-model="model.preferLogoOverIcon"
                    :name="$t('PreferLogoText')"
                    v-validate="''"
                    data-vv-validate-on="blur|input"
                  >
                  </vs-checkbox>
                </div>
              </div>

              <vs-divider></vs-divider>

              <div class="vx-row">
                <div class="vx-col w-1/2 flex items-center dropdown-tooltip-unclip">
                  <label class="font-bold">
                    <span class="inline-flex items-center whitespace-no-wrap">
                      <span>{{ $t('BrandColour') }}</span>
                      <vx-tooltip
                         :text="$t('BrandColourHelpText')"
                         position="top"
                         class="inline-block ml-2">
                         <feather-icon
                           icon="HelpCircleIcon"
                           svgClasses="h-4 w-4 hover:text-primary cursor-pointer"/>
                       </vx-tooltip>
                    </span>
                  </label>
                </div>

                <div class="vx-col w-1/2 flex items-center justify-end">
                  <div class="swatch-wrapper mr-2" aria-hidden="true">
                    <div
                      class="swatch"
                      :style="{ backgroundColor: model.brandColour }"
                    />
                    <input
                      v-model="brandColourHexInput"
                      class="color-input-overlay"
                      type="color"
                      :name="$t('BrandColour')"
                      @input="model.brandColour = $event.target.value"
                      aria-label="Pick brand colour"
                    />
                  </div>
                  <input
                    v-model="brandColourHexInput"
                    class="hex-input"
                    spellcheck="false"
                    placeholder="#RRGGBB"
                    maxlength="7"
                    @blur="applyHex('brand')"
                    @keydown.enter.prevent="applyHex('brand')"
                  />
                </div>
              </div>

              <div class="vx-row mt-base">
                <div class="vx-col w-1/2 flex items-center dropdown-tooltip-unclip">
                  <label class="font-bold">
                    <span class="inline-flex items-center whitespace-no-wrap">
                      <span>{{ $t('AccentColor') }}</span>
                      <vx-tooltip
                         :text="$t('AccentColorHelpText')"
                         position="top"
                         class="inline-block ml-2">
                         <feather-icon
                           icon="HelpCircleIcon"
                           svgClasses="h-4 w-4 hover:text-primary cursor-pointer"/>
                       </vx-tooltip>
                    </span>
                  </label>
                </div>
                <div class="vx-col w-1/2 flex items-center justify-end">
                  <div class="swatch-wrapper mr-2" aria-hidden="true">
                    <div
                      class="swatch"
                      :style="{ backgroundColor: model.accentColor }"
                    />
                    <input
                      v-model="accentColourHexInput"
                      class="color-input-overlay"
                      type="color"
                      :name="$t('AccentColor')"
                      @input="model.accentColor = $event.target.value"
                      aria-label="Pick accent colour"
                    />
                  </div>
                  <input
                    v-model="accentColourHexInput"
                    class="hex-input"
                    spellcheck="false"
                    placeholder="#RRGGBB"
                    maxlength="7"
                    @blur="applyHex('accent')"
                    @keydown.enter.prevent="applyHex('accent')"
                  />
                </div>
              </div>
            </div>

            <div
              v-if="!asWizardStep"
              class="w-full">
              <vs-button
                class="w-full mt-base xl:mt-0"
                @click="saveBranding()">
                {{ $t('SaveBrandingButtonText') }}
              </vs-button>
            </div>
          </div>
        </div>
        <div
          class="vx-col w-full branding-right-col mt-base xl:mt-0"
          >
          <div class="py-base px-base w-full" style="background: #f8f8f8">
            <div class="flex justify-between mb-6 w-full">
              <span class="w-full font-bold text-uppercase ml-base lg:ml-0">
                {{ 'Preview' | uppercase }}
              </span>

              <mq-layout mq="tablet+">
                <vs-switch
                  v-model="mobileMode"
                  vs-icon-off="desktop_windows"
                  vs-icon-on="smartphone"/>
              </mq-layout>
            </div>

            <div class="flex justify-center w-full">
              <phone-preview
                v-if="mobileMode">
                <waiting-list-form
                  :mobile-preview="true"
                  :brand-name="tenantAccountDetails.name"
                  :brand-colour="model.brandColour"
                  :accent-colour="model.accentColor"
                  :icon-path="model.icon"
                  :logo-path="model.logo"
                  :prefer-logo-over-icon="model.preferLogoOverIcon"
                />
              </phone-preview>

              <waiting-list-form
                v-else
                class="pointer-events-none"
                :desktop-preview="true"
                :brand-name="tenantAccountDetails.name"
                :brand-colour="model.brandColour"
                :accent-colour="model.accentColor"
                :icon-path="model.icon"
                :logo-path="model.logo"
                :prefer-logo-over-icon="model.preferLogoOverIcon"
              />
            </div>
          </div>
        </div>
      </div>
    </template>
  </vx-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import UploaderPreviewImage from '@/views/modules/_components/UploaderViewImage.vue';
import tenantService from '@/api/modules/tenant.service';
import PhonePreview from '@/views/modules/_components/PhonePreview.vue';
import WaitingListForm from '@/views/pages/HostedWaitingListForm.vue';
import DropdownTooltip from '@/views/modules/_components/DropdownTooltip.vue';
import themeConfig from '../../../../../../themeConfig';

/**
 * Account branding settings
 *
 * @module views/modules/user-account/UserAccountSettingsBranding
 * @author Dilan Useche <dilan8810@gmail.com>
 *
 */
export default {
  name: 'UserAccountBranding',
  i18n: {
    messages: {
      en: {
        TabTitle: 'Branding details',
        MenuSubtitle: 'Manage your branding details',
        FormTitle: 'Branding',
        BrandElementsTitle: 'Brand elements',
        BrandElementsText: 'Set your default brand elements to control how {businessName} looks and feels to your customers.',
        BrandingIcon: 'Icon',
        BrandingIconHelpText: 'A smaller representation of your logo (like a favicon). It must be square and at least 128px by 128px with a max size of 512KB. The supported MIME types are JPG, JPEG, PNG, SVG, GIF, and WEBP.',
        BrandingLogo: 'Logo',
        BrandingLogoHelpText: 'The full-sized version of your logo. It must be at least 128px by 128px with a max size of 512KB. The supported MIME types are JPG, JPEG, PNG, SVG, GIF, and WEBP.',
        PreferLogoText: 'Prefer logo over icon',
        PreferLogoHelpText: 'Use your logo instead of your icon where possible on surfaces such as the waiting list pick turn form. Test your changes using the preview feature.',
        BrandColour: 'Brand colour',
        BrandColourHelpText: 'Add a splash of colour to your branded pages.',
        AccentColor: 'Accent colour',
        AccentColorHelpText: 'Used for the main button colour across pages.',
        SaveBrandingButtonText: 'Save',
        UpdatedBrandingNotifyTitle: 'Updated branding details',
        UpdatedBrandingNotifyMsg: 'Branding logo updated successfully.',
      },
    },
  },
  components: {
    UploaderPreviewImage,
    PhonePreview,
    WaitingListForm,
    DropdownTooltip,
  },
  props: {
    asWizardStep: {
      type: Boolean,
      require: false,
      default: false,
    },
  },
  data() {
    return {
      payload: null,
      mobileMode: true,
      model: {
        icon: '',
        logo: '',
        preferLogoOverIcon: false,
        brandColour: themeConfig.colors.white,
        accentColor: themeConfig.colors.black,
      },
      acceptedFileExts: ['.jpg', '.jpeg', '.png', '.svg', '.gif', '.webp'],
      brandColourHexInput: themeConfig.colors.white,
      accentColourHexInput: themeConfig.colors.black,
    };
  },
  computed: {
    ...mapGetters({
      tenantAccountDetails: 'auth/tenantAccountDetails',
      basePath: 'auth/publicBasePathTenantToUploadResources',
    }),
    formWasChanged() {
      return Object.keys(this.validationFields).some(
        (key) => this.validationFields[key].changed,
      ) || (!!this.payload && (
        this.payload.icon !== this.model.icon
        || this.payload.logo !== this.model.logo
        || this.payload.preferLogoOverIcon !== this.model.preferLogoOverIcon
        || this.payload.brandColour !== this.model.brandColour
        || this.payload.accentColor !== this.model.accentColor
      ));
    },
  },
  watch: {
    'model.brandColour'(val) {
      this.brandColourHexInput = val ? val.toUpperCase() : val;
    },
    'model.accentColor'(val) {
      this.accentColourHexInput = val ? val.toUpperCase() : val;
    },
  },
  created() {
    this.fetchBrandingSettings();
  },
  methods: {
    ...mapActions({
      updateTenantAccountBrandingSettings: 'auth/updateTenantAccountBrandingSettings',
    }),
    async fetchBrandingSettings() {
      this.$vs.loading({ type: 'radius' });
      const resp = await tenantService.getAccountSettingsBranding();
      this.$vs.loading.close();
      this.payload = resp.data;
      this.setModel();
      await this.$validator.reset();
    },
    setModel() {
      this.model.icon = this.payload
        ? this.payload.icon || this.model.icon
        : this.model.icon;

      this.model.logo = this.payload
        ? this.payload.logo || this.model.logo
        : this.model.logo;

      this.model.preferLogoOverIcon = this.payload
        ? this.payload.preferLogoOverIcon || this.model.preferLogoOverIcon
        : this.model.preferLogoOverIcon;

      this.model.brandColour = this.payload
        ? this.payload.brandColour || this.model.brandColour
        : this.model.brandColour;

      this.model.brandColour = this.payload
        ? this.payload.brandColour || this.model.brandColour
        : this.model.brandColour;

      this.model.accentColor = this.payload
        ? this.payload.accentColor || this.model.accentColor
        : this.model.accentColor;

      // sync hex inputs
      this.brandColourHexInput = this.model.brandColour ? this.model.brandColour.toUpperCase() : this.model.brandColour;
      this.accentColourHexInput = this.model.accentColor ? this.model.accentColor.toUpperCase() : this.model.accentColor;
    },
    async saveBranding() {
      this.$vs.loading({ type: 'radius' });
      const resp = await this.updateTenantAccountBrandingSettings(this.model);
      this.$vs.loading.close();
      this.payload = resp.data;
      this.setModel();

      this.$showSuccessActionNotification({
        title: this.$t('UpdatedBrandingNotifyTitle'),
        text: this.$t('UpdatedBrandingNotifyMsg'),
      });
    },
    applyHex(which) {
      let val = which === 'brand' ? this.brandColourHexInput : this.accentColourHexInput;
      val = (val || '').trim();

      if (val.startsWith('#')) {
        val = val.substring(1);
      }

      if (val.length === 3) {
        val = val.split('').map((char) => char + char).join('');
      }

      const hex = `#${val.toUpperCase()}`;
      const valid = /^#([0-9A-F]{6})$/.test(hex);

      if (!valid) {
        // revert
        if (which === 'brand') this.brandColourHexInput = this.model.brandColour;
        else this.accentColourHexInput = this.model.accentColor;
        return;
      }
      if (which === 'brand') {
        this.model.brandColour = hex;
        this.brandColourHexInput = hex;
      } else {
        this.model.accentColor = hex;
        this.accentColourHexInput = hex;
      }
    },
  },
};
</script>

<style scoped>

@media (min-width: 1200px) {
  .branding-left-col {
    flex: 0 0 420px; /* do not shrink; fixed column width */
    max-width: 420px;
  }
  .branding-right-col {
    flex: 1 1 auto; /* occupy remaining space so it stays alongside */
    max-width: calc(100% - 420px);
  }
}

.swatch-wrapper {
  position: relative;
  width: 36px;
  height: 36px;
}
.swatch {
  width: 36px;
  height: 36px;
  border-radius: 6px;
  border: 2px solid #ffffff;
  box-shadow: 0 0 0 1px #cfcfcf;
}
.color-input-overlay {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
}

.hex-input {
  width: 88px; /* fits #RRGGBB comfortably */
  height: 32px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 4px 8px;
  font-size: 12px;
}
</style>
